import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Outlet, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ModeNavigation from '../../components/learning/ModeNavigation';
import NoteEditor from '../../components/learning/NoteEditor';
import AskQuestion from '../../components/learning/AskQuestion';
import DeleteConfirmModal from '../../components/DeleteConfirmModal';
import { useKeyboardHeight } from '../../hooks/useKeyboardHeight';
import { coursesService, FullCourseData, Episode } from '../../services/coursesService';
import { creatorApiClient } from '../../services/apiClient';
import { Note } from '../../types/learning';
import './LearningLayout.css';
import { BilingualTitle } from '../../components/common/BilingualTitle';

// Current segment context for AI Assistant
export interface CurrentSegmentContext {
  segmentId?: string; // Stable UUID — required for per-sentence FAQ + creator escalation
  sentence: string;
  translation?: string;
  vocabulary?: string[];
  grammarPoints?: { title: string; explanation: string }[];
  chunks?: { text: string; meaning: string }[];
}

// Pre-filled note content for quick note creation
export interface PrefilledNoteContent {
  content: string;
  relatedWord?: string;
  tags?: string[];
  segmentId?: string;
  sentenceIndex?: number;
}

// Episode info for navigation
export interface EpisodeInfo {
  index: number;
  title: string;
  segmentCount: number;
  durationSeconds?: number;
}

// Context for sharing course data and current segment with child pages
interface LearningContextType {
  courseData: FullCourseData | null;
  loading: boolean;
  error: string | null;
  // Current segment context for AI Assistant (smart context selection)
  currentSegmentContext: CurrentSegmentContext | null;
  setCurrentSegmentContext: (context: CurrentSegmentContext | null) => void;
  // Open note editor with pre-filled content
  openNoteWithContent: (prefilled: PrefilledNoteContent) => void;
  // Episode navigation
  episodes: EpisodeInfo[];
  selectedEpisodeIndex: number | null; // null = all episodes (single-episode course)
  setSelectedEpisodeIndex: (index: number) => void;
}

const LearningContext = createContext<LearningContextType>({
  courseData: null,
  loading: true,
  error: null,
  currentSegmentContext: null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setCurrentSegmentContext: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  openNoteWithContent: () => {},
  episodes: [],
  selectedEpisodeIndex: null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setSelectedEpisodeIndex: () => {},
});

export const useLearningContext = () => useContext(LearningContext);

const LearningLayout: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation(['learning', 'common']);
  useKeyboardHeight(); // Detect mobile keyboard height for all learning modes
  const [rawCourseData, setRawCourseData] = useState<FullCourseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNotes, setShowNotes] = useState(false);
  const [showNoteEditor, setShowNoteEditor] = useState(false);
  const [notes, setNotes] = useState<Note[]>([]);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<Note | null>(null);
  const [expandedNoteIds, setExpandedNoteIds] = useState<Set<string>>(new Set());
  const [currentSegmentContext, setCurrentSegmentContext] = useState<CurrentSegmentContext | null>(null);
  const [prefilledNoteContent, setPrefilledNoteContent] = useState<PrefilledNoteContent | null>(null);

  // Auto-hide header/footer on scroll (mobile only)
  const [barsHidden, setBarsHidden] = useState(false);
  const mainRef = useRef<HTMLElement>(null);
  const lastScrollTop = useRef(0);
  const scrollThreshold = 10; // minimum scroll delta to trigger hide/show

  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;

    const handleScroll = () => {
      const st = el.scrollTop;
      const maxScroll = el.scrollHeight - el.clientHeight;
      const delta = st - lastScrollTop.current;

      // Show bars at top or bottom edges
      if (st <= 0 || st >= maxScroll - 5) {
        setBarsHidden(false);
      } else if (delta > scrollThreshold) {
        // Scrolling down — hide
        setBarsHidden(true);
      } else if (delta < -scrollThreshold) {
        // Scrolling up — show
        setBarsHidden(false);
      }

      lastScrollTop.current = st;
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchCourseData = async () => {
      if (!courseId) return;

      setLoading(true);
      setError(null);

      try {
        const data = await coursesService.getFullCourse(courseId);
        setRawCourseData(data);
      } catch (err: any) {
        console.error('Failed to fetch course data:', err);
        setError(err.message || t('common:failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    fetchCourseData();
  }, [courseId, t]);

  // Compute episode info from sections data
  const episodes: EpisodeInfo[] = useMemo(() => {
    if (!rawCourseData?.sections) return [];
    const eps: EpisodeInfo[] = [];
    for (const section of rawCourseData.sections) {
      for (const episode of section.episodes || []) {
        eps.push({
          index: episode.orderIndex,
          title: episode.title,
          segmentCount: episode.segments?.length || 0,
          durationSeconds: episode.durationSeconds,
        });
      }
    }
    return eps.sort((a, b) => a.index - b.index);
  }, [rawCourseData?.sections]);

  // Episode selection from URL param
  const episodeParam = searchParams.get('ep');
  const isMultiEpisode = episodes.length > 1;
  const selectedEpisodeIndex = isMultiEpisode
    ? (episodeParam !== null ? parseInt(episodeParam, 10) : 0)
    : null;

  // Auto-set episode=0 in URL for multi-episode courses when not specified
  useEffect(() => {
    if (isMultiEpisode && episodeParam === null) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('ep', '0');
        return next;
      }, { replace: true });
    }
  }, [isMultiEpisode, episodeParam, setSearchParams]);

  const setSelectedEpisodeIndex = useCallback((index: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('ep', String(index));
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Filter courseData by selected episode
  const courseData: FullCourseData | null = useMemo(() => {
    if (!rawCourseData) return null;
    // Single episode or no filter → return all data
    if (selectedEpisodeIndex === null || !rawCourseData.sections) return rawCourseData;

    // Compute sentence index ranges per episode
    const allEpisodes: Episode[] = [];
    for (const section of rawCourseData.sections) {
      for (const episode of section.episodes || []) {
        allEpisodes.push(episode);
      }
    }
    allEpisodes.sort((a, b) => a.orderIndex - b.orderIndex);

    // Calculate start index for each episode
    let startIdx = 0;
    let filteredSentences = rawCourseData.sentences;
    let filteredSections = rawCourseData.sections;

    for (const ep of allEpisodes) {
      const count = ep.segments?.length || 0;
      if (ep.orderIndex === selectedEpisodeIndex) {
        // Slice sentences for this episode only
        if (rawCourseData.sentences) {
          filteredSentences = rawCourseData.sentences.slice(startIdx, startIdx + count);
        }
        // Filter sections to only include this episode
        filteredSections = rawCourseData.sections.map(section => ({
          ...section,
          episodes: (section.episodes || []).filter(e => e.orderIndex === selectedEpisodeIndex),
        })).filter(s => s.episodes.length > 0);
        break;
      }
      startIdx += count;
    }

    return {
      ...rawCourseData,
      sentences: filteredSentences,
      sections: filteredSections,
    };
  }, [rawCourseData, selectedEpisodeIndex]);

  // Fetch notes for this course
  useEffect(() => {
    const fetchNotes = async () => {
      if (!courseId) return;
      try {
        const response = await creatorApiClient.get<Note[]>(`/learning/courses/${courseId}/notes`);
        setNotes(response.data);
      } catch (err) {
        console.error('Failed to fetch notes:', err);
      }
    };
    fetchNotes();
  }, [courseId]);

  const handleBackClick = () => {
    // Navigate back to course detail page
    navigate(`/courses/${courseId}`);
  };

  const handleNotesClick = () => {
    setShowNotes(!showNotes);
  };

  const handleAddNote = () => {
    setEditingNote(null);
    setPrefilledNoteContent(null);
    setShowNoteEditor(true);
  };

  // Open note editor with pre-filled content (for quick note from pronunciation tips, etc.)
  const openNoteWithContent = useCallback((prefilled: PrefilledNoteContent) => {
    setEditingNote(null);
    setPrefilledNoteContent(prefilled);
    setShowNoteEditor(true);
    setShowNotes(true); // Also open notes panel
  }, []);

  const handleEditNote = (note: Note) => {
    setEditingNote(note);
    setShowNoteEditor(true);
  };

  const toggleNoteExpanded = (noteId: string) => {
    setExpandedNoteIds(prev => {
      const next = new Set(prev);
      if (next.has(noteId)) next.delete(noteId);
      else next.add(noteId);
      return next;
    });
  };

  const handleSaveNote = useCallback(async (noteData: Partial<Note>) => {
    if (!courseId) return;

    try {
      let savedNote: Note;
      if (editingNote) {
        const response = await creatorApiClient.patch<Note>(`/learning/notes/${editingNote.id}`, noteData);
        savedNote = response.data;
        setNotes(notes.map(n => n.id === savedNote.id ? savedNote : n));
      } else {
        // Include segmentId and sentenceIndex from prefilled content if available
        const createData = {
          ...noteData,
          courseId,
          ...(prefilledNoteContent?.segmentId && { segmentId: prefilledNoteContent.segmentId }),
          ...(prefilledNoteContent?.sentenceIndex !== undefined && { sentenceIndex: prefilledNoteContent.sentenceIndex }),
        };
        const response = await creatorApiClient.post<Note>('/learning/notes', createData);
        savedNote = response.data;
        setNotes([savedNote, ...notes]);
      }
      setShowNoteEditor(false);
      setEditingNote(null);
      setPrefilledNoteContent(null);
    } catch (err) {
      console.error('Failed to save note:', err);
      alert(t('common:failed'));
    }
  }, [courseId, editingNote, notes, prefilledNoteContent, t]);

  const handleDeleteNoteClick = (note: Note) => {
    setNoteToDelete(note);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!noteToDelete) return;

    try {
      await creatorApiClient.delete(`/learning/notes/${noteToDelete.id}`);
      setNotes(notes.filter(n => n.id !== noteToDelete.id));
    } catch (err) {
      console.error('Failed to delete note:', err);
    } finally {
      setDeleteModalOpen(false);
      setNoteToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setNoteToDelete(null);
  };

  return (
    <LearningContext.Provider value={{ courseData, loading, error, currentSegmentContext, setCurrentSegmentContext, openNoteWithContent, episodes, selectedEpisodeIndex, setSelectedEpisodeIndex }}>
      <div className={`learning-layout ${barsHidden ? 'learning-layout--bars-hidden' : ''}`}>
        {/* Unified Header — back + title + episodes + notes in one surface */}
        <header className="learning-layout__header">
          <button className="learning-layout__back" onClick={handleBackClick}>
            ←
          </button>
          <BilingualTitle
            title={courseData?.course?.title || ''}
            titles={courseData?.course?.titles}
            fallback={t('common:loading')}
            variant="compact"
            as="h1"
            className="learning-layout__title"
          />

          {/* Episode selector inline — only for multi-episode courses */}
          {isMultiEpisode && selectedEpisodeIndex !== null && (
            <div className="learning-layout__episode-inline">
              <button
                className="episode-bar__arrow"
                disabled={selectedEpisodeIndex <= 0}
                onClick={() => setSelectedEpisodeIndex(selectedEpisodeIndex - 1)}
              >
                ‹
              </button>
              <div className="episode-bar__selector">
                {episodes.map((ep) => (
                  <button
                    key={ep.index}
                    className={`episode-bar__pill ${ep.index === selectedEpisodeIndex ? 'active' : ''}`}
                    onClick={() => setSelectedEpisodeIndex(ep.index)}
                    title={ep.title}
                  >
                    <span className="episode-bar__pill-num">{ep.index + 1}</span>
                    <span className="episode-bar__pill-title">{ep.title}</span>
                  </button>
                ))}
              </div>
              <button
                className="episode-bar__arrow"
                disabled={selectedEpisodeIndex >= episodes.length - 1}
                onClick={() => setSelectedEpisodeIndex(selectedEpisodeIndex + 1)}
              >
                ›
              </button>
            </div>
          )}

          <button
            className={`learning-layout__notes-btn ${showNotes ? 'active' : ''}`}
            onClick={handleNotesClick}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="2" width="10" height="12" rx="1.5" />
              <path d="M5.5 5.5h5M5.5 8h5M5.5 10.5h3" />
            </svg>
            {notes.length > 0 && <span className="learning-layout__notes-count">{notes.length}</span>}
          </button>
        </header>

        {/* Main Content Area */}
        <div className="learning-layout__content">
          <ModeNavigation />
          <main className="learning-layout__main" ref={mainRef}>
            <Outlet />
            {/* Spacer so the last bit of content can scroll above the fixed
                mobile mode-navigation. Hidden on desktop and in the
                comprehension immersive mode via CSS. */}
            <div className="learning-layout__mobile-padding" aria-hidden="true" />
          </main>

          {/* Notes Panel */}
          {showNotes && (
            <>
            <div className="learning-layout__notes-backdrop" onClick={() => setShowNotes(false)} />
            <aside className="learning-layout__notes-panel">
              <div className="notes-panel__header">
                <button className="notes-panel__back-btn" onClick={() => setShowNotes(false)}>
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4L6 9l5 5" />
                  </svg>
                </button>
                <h3>{t('layout.courseNotes')}</h3>
                <button className="notes-panel__add-btn" onClick={handleAddNote}>
                  + {t('layout.addNote')}
                </button>
              </div>
              <div className="notes-panel__list">
                {notes.length === 0 ? (
                  <div className="notes-panel__empty">
                    <p>{t('layout.noNotes')}</p>
                    <p className="notes-panel__hint">{t('layout.noNotesHint')}</p>
                  </div>
                ) : (
                  notes.map(note => {
                    const isLong = note.content.length > 100;
                    const isExpanded = expandedNoteIds.has(note.id);
                    const displayContent = isLong && !isExpanded
                      ? `${note.content.substring(0, 100)}...`
                      : note.content;
                    return (
                      <div key={note.id} className="notes-panel__item">
                        <div className={`notes-panel__item-content ${isExpanded ? 'expanded' : ''}`}>
                          {displayContent}
                        </div>
                        {isLong && (
                          <button
                            type="button"
                            className="notes-panel__item-toggle"
                            onClick={() => toggleNoteExpanded(note.id)}
                          >
                            {isExpanded ? t('layout.collapse') : t('layout.showMore')}
                          </button>
                        )}
                        {note.tags && note.tags.length > 0 && (
                          <div className="notes-panel__item-tags">
                            {note.tags.map(tag => (
                              <span key={tag} className="notes-panel__tag">#{tag}</span>
                            ))}
                          </div>
                        )}
                        <div className="notes-panel__item-actions">
                          <button onClick={() => handleEditNote(note)}>{t('layout.edit')}</button>
                          <button onClick={() => handleDeleteNoteClick(note)}>{t('layout.delete')}</button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </aside>
            </>
          )}
        </div>

        {/* Note Editor Modal */}
        {showNoteEditor && (
          <NoteEditor
            note={editingNote}
            onSave={handleSaveNote}
            onClose={() => {
              setShowNoteEditor(false);
              setEditingNote(null);
              setPrefilledNoteContent(null);
            }}
            availableTags={['vocabulary', 'grammar', 'pronunciation', 'culture', 'tips']}
            prefilledContent={prefilledNoteContent}
          />
        )}

        {/* Delete Confirmation Modal */}
        <DeleteConfirmModal
          isOpen={deleteModalOpen}
          title={t('layout.deleteNoteTitle')}
          message={t('layout.deleteNoteMessage')}
          itemName={noteToDelete?.content.substring(0, 50) + (noteToDelete?.content && noteToDelete.content.length > 50 ? '...' : '')}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />

        {/* AI Question Assistant */}
        <AskQuestion
          courseId={courseId}
          segmentId={currentSegmentContext?.segmentId}
          currentSentence={currentSegmentContext?.sentence}
          translation={currentSegmentContext?.translation}
          vocabulary={currentSegmentContext?.vocabulary}
          grammarPoints={currentSegmentContext?.grammarPoints}
          chunks={currentSegmentContext?.chunks}
          // Deep-link from "creator answered your question" notification —
          // panel auto-opens on land so the learner sees the FAQ card right away.
          autoOpen={searchParams.get('showAnswer') === '1'}
        />
      </div>
    </LearningContext.Provider>
  );
};

export default LearningLayout;